import type { ProspectMatterType } from '@common/enums';
import { getContactName } from '@common/utils/contactNames';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import type { MouseEvent } from 'react';
import { useMemo } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import EditableViewComponentLeftAligned from '@/components/ReadOnlyComponent/EditableViewComponentLeftAligned';
import RenderCriticalQuestion from '@/components/RenderCriticalQuestion';
import {
    CONTACT_DETAILS,
    CURRENT_ADDRESS,
    dateRegex,
    dateStringRegex,
    INITIAL_DETAILS,
    MAILING_INFO,
    ordinalNumbers,
    PROPERTY_DETAILS,
} from '@/constants';
import { errorSnackbar } from '@/utils/snackbar';
import DraftIntakeDisclaimer from '../DraftIntakeDisclaimer';
import IntakeResponseTable from './IntakeResponseTable';
import type { IntakeResponseObj } from './types';

type RealEstateMatterType = Extract<
    ProspectMatterType,
    'Sale' | 'Purchase' | 'Refinance' | 'PrivateLender'
>;

interface RealEstateIntakeConfig {
    contactSectionTitle: string;
    contactPartyLabel: string;
    contactAccordionKey: string;
    addressGroupKey: string;
    addressSectionTitle: string;
    filterAddressByFormGroupTitle: boolean;
}

const REAL_ESTATE_INTAKE_CONFIG: Record<RealEstateMatterType, RealEstateIntakeConfig> = {
    Sale: {
        contactSectionTitle: 'Seller(s) info',
        contactPartyLabel: 'seller',
        contactAccordionKey: 'seller',
        addressGroupKey: MAILING_INFO,
        addressSectionTitle: 'Mailing Info',
        filterAddressByFormGroupTitle: false,
    },
    Purchase: {
        contactSectionTitle: 'Purchaser(s) info',
        contactPartyLabel: 'purchaser',
        contactAccordionKey: 'purchaser',
        addressGroupKey: MAILING_INFO,
        addressSectionTitle: 'Mailing Info',
        filterAddressByFormGroupTitle: true,
    },
    Refinance: {
        contactSectionTitle: 'Borrower(s) info',
        contactPartyLabel: 'borrower',
        contactAccordionKey: 'borrower',
        addressGroupKey: CURRENT_ADDRESS,
        addressSectionTitle: 'Current Address',
        filterAddressByFormGroupTitle: false,
    },
    PrivateLender: {
        contactSectionTitle: 'Lender(s) info',
        contactPartyLabel: 'lender',
        contactAccordionKey: 'lender',
        addressGroupKey: CURRENT_ADDRESS,
        addressSectionTitle: 'Current Address',
        filterAddressByFormGroupTitle: false,
    },
};

const excludeDateFormatting: string[] = [];

function readNamedField(fields: IntakeResponseObj[], fieldName: string): string {
    const value = fields.find((field) => field.Name === fieldName)?.Value;
    if (typeof value !== 'string') return '';

    const trimmed = value.trim();
    if (!trimmed || trimmed === '-' || trimmed === 'NA') return '';

    return trimmed;
}

function getContactAccordionTitle(fields: IntakeResponseObj[], fallback: string): string {
    const firstName = readNamedField(fields, 'firstName');
    const middleName = readNamedField(fields, 'middleName');
    const lastName = readNamedField(fields, 'lastName');

    if (firstName && lastName) {
        return getContactName({ firstName, middleName, lastName });
    }

    const partialName = [firstName, middleName, lastName].filter(Boolean).join(' ');
    return partialName || fallback;
}

interface Props {
    matterType: RealEstateMatterType;
    responseDetailsContainerStyles: object;
    isIntakePdfDownloading: boolean;
    intakeResponseData: object;
    accordionStyles: object;
    prospectId: string | undefined;
    accordionDetailsStyles: object;
    handleDocumentDownload: (fileUrl: string, fileName: string) => Promise<void>;
    setIsIntakePdfDownloading: (downloading: boolean) => void;
    handleIntakePdfDownload: (prospectId: string | undefined) => void;
    isDraft?: boolean;
    draftLastUpdatedAt?: Date | string;
}

function formatFieldValue(
    field: IntakeResponseObj,
    useContactDateRegex: boolean,
): IntakeResponseObj {
    const datePattern = useContactDateRegex ? dateRegex : dateStringRegex;
    if (datePattern.test(field.Value) && !excludeDateFormatting.includes(field.Name)) {
        return {
            ...field,
            Value: DateTime.fromJSDate(new Date(field.Value)).toFormat('MM/dd/yyyy'),
        };
    }
    return field;
}

export default function RealEstateIntakeInformation({
    matterType,
    responseDetailsContainerStyles,
    isIntakePdfDownloading,
    intakeResponseData,
    accordionStyles,
    prospectId,
    accordionDetailsStyles,
    handleDocumentDownload,
    setIsIntakePdfDownloading,
    handleIntakePdfDownload,
    isDraft = false,
    draftLastUpdatedAt,
}: Props) {
    const config = REAL_ESTATE_INTAKE_CONFIG[matterType];

    const filteredInitialDetails = useMemo(
        () =>
            (intakeResponseData[INITIAL_DETAILS] as IntakeResponseObj[] | undefined)
                ?.filter((field) => field.Value)
                ?.sort((a, b) => a.DisplayOrder - b.DisplayOrder) ?? [],
        [intakeResponseData],
    );

    const filteredContactDetails = useMemo(
        () =>
            (intakeResponseData[CONTACT_DETAILS] as IntakeResponseObj[][] | undefined)?.filter(
                (contact) => contact?.some((field) => field.Value),
            ) ?? [],
        [intakeResponseData],
    );

    const filteredAddressInfo = useMemo(() => {
        const fields = intakeResponseData[config.addressGroupKey] as
            | IntakeResponseObj[]
            | undefined;
        return (
            fields
                ?.filter((field) => {
                    if (!field.Value) return false;
                    if (config.filterAddressByFormGroupTitle) {
                        return field.FormGroupTitle === config.addressGroupKey;
                    }
                    return true;
                })
                ?.sort((a, b) => a.DisplayOrder - b.DisplayOrder) ?? []
        );
    }, [config.addressGroupKey, config.filterAddressByFormGroupTitle, intakeResponseData]);

    const filteredPropertyDetails = useMemo(
        () =>
            (intakeResponseData[PROPERTY_DETAILS] as IntakeResponseObj[] | undefined)
                ?.filter((field) => field.Value)
                ?.sort((a, b) => a.DisplayOrder - b.DisplayOrder) ?? [],
        [intakeResponseData],
    );

    const handlePrintClick = async () => {
        if (!prospectId) return;
        setIsIntakePdfDownloading(true);
        try {
            handleIntakePdfDownload(prospectId);
        } catch (err) {
            console.error(err);
            errorSnackbar('Error downloading the file');
        } finally {
            setIsIntakePdfDownloading(false);
        }
    };

    const getOnLinkClickHandler = (fieldId?: string, value?: string) => {
        if (!fieldId) return undefined;

        return (event: MouseEvent) => {
            event.preventDefault();
            handleDocumentDownload(
                `${import.meta.env.VITE_APP_API_URL}/api/v1/files/${fieldId}`,
                value ?? 'document',
            );
        };
    };

    const renderField = (
        field: IntakeResponseObj,
        options: { useContactDateRegex?: boolean; reactKey?: string } = {},
    ) => {
        const { useContactDateRegex = false, reactKey } = options;

        if (field._table) {
            return (
                <IntakeResponseTable
                    key={reactKey ?? field.Title}
                    title={field.Title}
                    isCriticalQuestion={field.IsCriticalQuestion}
                    columns={field._table.columns}
                    rows={field._table.rows}
                />
            );
        }

        const formattedField = formatFieldValue(field, useContactDateRegex);
        const isUploadField = !!formattedField._fileId;

        return (
            <EditableViewComponentLeftAligned
                key={reactKey ?? formattedField.Title}
                label={
                    <RenderCriticalQuestion
                        title={formattedField.Title}
                        isCritical={formattedField.IsCriticalQuestion}
                    />
                }
                value={formattedField.Value || '-'}
                isCopyable={!isUploadField}
                link={isUploadField ? '#' : undefined}
                onLinkClick={getOnLinkClickHandler(formattedField._fileId, formattedField.Value)}
            />
        );
    };

    return (
        <Box
            sx={{
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
            }}
        >
            {isDraft && <DraftIntakeDisclaimer draftLastUpdatedAt={draftLastUpdatedAt} />}
            {filteredInitialDetails.length > 0 && (
                <Box sx={responseDetailsContainerStyles}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                        }}
                    >
                        <Typography variant="h3">Initial details</Typography>
                        {!isDraft && (
                            <Button variant="contained" onClick={handlePrintClick}>
                                {isIntakePdfDownloading ? (
                                    <LoadingButton loading variant="outlined" />
                                ) : (
                                    <PrintOutlinedIcon />
                                )}
                            </Button>
                        )}
                    </Box>
                    {filteredInitialDetails.map((field) => renderField(field))}
                </Box>
            )}
            {filteredContactDetails.length > 0 && (
                <Box sx={responseDetailsContainerStyles}>
                    <Typography variant="h3">{config.contactSectionTitle}</Typography>
                    {filteredContactDetails.map((contactInfo, index) => (
                        <Accordion
                            key={`${config.contactAccordionKey}_${index}`}
                            defaultExpanded
                            sx={accordionStyles}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="labelLarge" color="text.primary">
                                    {getContactAccordionTitle(
                                        contactInfo,
                                        `${ordinalNumbers[index] ?? `${index + 1}th`} ${config.contactPartyLabel} info`,
                                    )}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={accordionDetailsStyles}>
                                {contactInfo
                                    ?.filter((field) => field.Value)
                                    ?.sort((a, b) => a.DisplayOrder - b.DisplayOrder)
                                    ?.map((field, fieldIndex) => (
                                        <Box key={fieldIndex}>
                                            {renderField(field, {
                                                useContactDateRegex: true,
                                                reactKey: field.Name,
                                            })}
                                        </Box>
                                    ))}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}
            {filteredAddressInfo.length > 0 && (
                <Box sx={responseDetailsContainerStyles}>
                    <Typography variant="h3">{config.addressSectionTitle}</Typography>
                    {filteredAddressInfo.map((field) => renderField(field))}
                </Box>
            )}
            {filteredPropertyDetails.length > 0 && (
                <Box sx={responseDetailsContainerStyles}>
                    <Typography variant="h3">Property Details</Typography>
                    {filteredPropertyDetails.map((field) => renderField(field))}
                </Box>
            )}
        </Box>
    );
}
